/**
 * Navigation Component
 * Coláiste Lurgan Management System - Role-based navigation menu
 * Access controlled by Page Permissions system (database-driven)
 */

import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { cn } from '../../lib/utils';
import { useUserPermissions } from '../../hooks/useAuth';
import api from '../../lib/api';
import {
  Users,
  Mail,
  Settings,
  BarChart3,
  Home,
  Building2,
  LayoutGrid,
  UserCog,
  CreditCard,
  GraduationCap,
  Shield,
} from 'lucide-react';

// Navigation items - access controlled by Page Permissions API
const navigationItems = [
  { titleKey: 'nav.dashboard', href: '/dashboard', icon: BarChart3 },
  { titleKey: 'nav.familyDashboard', href: '/family-dashboard', icon: Home, parentOnly: true },
  { titleKey: 'nav.students', href: '/students', icon: Users },
  { titleKey: 'nav.houses', href: '/houses', icon: Building2 },
  { titleKey: 'nav.houseAllocation', href: '/house-allocation', icon: LayoutGrid },
  { titleKey: 'nav.cuntoirs', href: '/cuntoirs', icon: UserCog },
  { titleKey: 'nav.classAllocation', href: '/class-allocation', icon: GraduationCap },
  { titleKey: 'nav.payments', href: '/payments', icon: CreditCard, allowedTypes: ['ADMIN', 'OFFICE'] },
  { titleKey: 'nav.communications', href: '/communications', icon: Mail },
];

const settingsItems = [
  { titleKey: 'nav.settings', href: '/settings', icon: Settings },
  { titleKey: 'nav.auditLog', href: '/audit-log', icon: Shield, adminOnly: true },
];

export function Navigation({ mediumExpanded = false, onMobileNavClick }) {
  const { t } = useTranslation();
  const { userType } = useUserPermissions();

  // Fetch accessible pages from Page Permissions API
  const { data: accessiblePagesData } = useQuery({
    queryKey: ['user', 'pages'],
    queryFn: api.pagePermissions.getMyPages,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userType,
  });

  const canAccessItem = (item) => {
    // Parent-only items are exclusively for PARENT users (ADMIN bypass does not apply)
    if (item.parentOnly) {
      return userType === 'PARENT';
    }

    // Admin-only items (e.g. Audit Log)
    if (item.adminOnly) {
      return userType === 'ADMIN';
    }

    // ADMIN users have access to everything else
    if (userType === 'ADMIN') {
      return true;
    }

    // Get page key from href (e.g., '/dashboard' -> 'dashboard')
    const pageKey = item.href.replace('/', '');

    // Check against Page Permissions API
    const pages = accessiblePagesData?.pages || [];
    return pages.includes(pageKey);
  };

  const NavItem = ({ item, isSettings = false }) => {
    const Icon = item.icon;

    return (
      <NavLink
        to={item.href}
        onClick={onMobileNavClick}
        style={({ isActive }) =>
          isActive ? { background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' } : {}
        }
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-lg transition-colors',
            mediumExpanded ? 'justify-start' : 'xl:justify-start lg:justify-center justify-start',
            isActive
              ? 'text-gray-900'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            isSettings && 'mt-2'
          )
        }
        title={!mediumExpanded ? t(item.titleKey) : undefined}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className={cn(
          'transition-all duration-200',
          // Show text: always on mobile/XL, or when medium is expanded
          mediumExpanded 
            ? 'block' 
            : 'xl:block lg:hidden block' // XL: show, LG: hide, smaller: show
        )}>
          {t(item.titleKey)}
        </span>
      </NavLink>
    );
  };

  return (
    <nav className="p-4 space-y-2">
      {/* Main Navigation */}
      <div className="space-y-1">
        {navigationItems
          .filter(canAccessItem)
          .map((item) => (
            <NavItem key={item.href} item={item} />
          ))}
      </div>

      {/* Separator */}
      {navigationItems.filter(canAccessItem).length > 0 && 
       settingsItems.filter(canAccessItem).length > 0 && (
        <div className="border-t my-4" />
      )}

      {/* Settings Navigation */}
      <div className="space-y-1">
        {settingsItems
          .filter(canAccessItem)
          .map((item) => (
            <NavItem key={item.href} item={item} isSettings />
          ))}
      </div>

    </nav>
  );
}

export default Navigation;